import { API_BASE_URL, resolvePublicMediaUrl } from '../config/api';
import { parseNewsListMeta } from './cmsNews';
import { splitMemberFullName } from './membersDirectory';

const PAGE_SIZE = 500;
const MAX_PAGES = 50;

/**
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<{ list: unknown[], metaData: unknown }>}
 */
async function parseListResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`${failLabel} (yanıt okunamadı)`);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }

    const raw = /** @type {{ data?: unknown, metaData?: unknown }} */ (obj)
        .data;
    /** @type {unknown[]} */
    let list;
    if (Array.isArray(raw)) {
        list = raw;
    } else if (raw != null && typeof raw === 'object') {
        list = [raw];
    } else {
        list = [];
    }
    const metaData =
        /** @type {{ metaData?: unknown }} */ (obj).metaData ?? null;

    return { list, metaData };
}

/**
 * @param {unknown} row
 * @returns {import('../data/members').ClubMember | null}
 */
export function mapGroupMemberToClubMember(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    const { givenName, familyName } = splitMemberFullName(
        String(r.fullName ?? ''),
    );
    const subs = r.subMembers;
    const sub =
        Array.isArray(subs) && subs[0] && typeof subs[0] === 'object'
            ? /** @type {Record<string, unknown>} */ (subs[0])
            : null;

    let partnerGivenName;
    let partnerFamilyName;
    /** @type {string | undefined} */
    let partnerProfilePictureUrl;
    if (sub?.fullName) {
        const p = splitMemberFullName(String(sub.fullName));
        partnerGivenName = p.givenName;
        partnerFamilyName = p.familyName;
    }
    if (sub?.profilePicture) {
        partnerProfilePictureUrl = resolvePublicMediaUrl(
            String(sub.profilePicture),
        );
    }
    const partnerId =
        sub?.id != null && String(sub.id).trim()
            ? String(sub.id).trim()
            : undefined;

    const id = String(r.id ?? '');
    if (!id) return null;

    const profilePictureUrl = r.profilePicture
        ? resolvePublicMediaUrl(String(r.profilePicture))
        : undefined;

    return {
        id,
        givenName,
        familyName,
        partnerGivenName,
        partnerFamilyName,
        partnerId,
        partnerProfilePictureUrl,
        profilePictureUrl,
        birthDate: '',
        joinedAt: '',
        profession: '',
        email: '',
    };
}

/**
 * @typedef {{
 *   id: string,
 *   name: string,
 *   description: string,
 *   leaderId: string | null,
 *   members: import('../data/members').ClubMember[],
 * }} MemberGroupUi
 */

/**
 * @param {unknown} row
 * @returns {MemberGroupUi | null}
 */
export function mapApiGroupToUi(row) {
    if (!row || typeof row !== 'object') return null;
    const g = /** @type {Record<string, unknown>} */ (row);
    let id = String(g.id ?? '').trim();
    if (!id) {
        const namePart = String(g.name ?? 'grup').slice(0, 120);
        const leader = String(g.leaderId ?? '').trim();
        id = `__noid__${namePart}__${leader}`;
    }

    const leaderIdNorm =
        g.leaderId != null && String(g.leaderId).trim()
            ? String(g.leaderId).trim().toLowerCase()
            : null;

    /** @type {import('../data/members').ClubMember[]} */
    const members = [];
    const rawMembers = g.members;
    if (Array.isArray(rawMembers)) {
        for (const m of rawMembers) {
            const cm = mapGroupMemberToClubMember(m);
            if (!cm) continue;
            const isLeader =
                leaderIdNorm != null &&
                cm.id.trim().toLowerCase() === leaderIdNorm;
            members.push(
                isLeader ? { ...cm, groupLeader: true } : cm,
            );
        }
    }

    return {
        id,
        name: String(g.name || 'Grup'),
        description:
            g.description != null && String(g.description).trim()
                ? String(g.description).trim()
                : '',
        leaderId:
            g.leaderId != null && String(g.leaderId).trim()
                ? String(g.leaderId).trim()
                : null,
        members,
    };
}

/**
 * @param {string} accessToken
 * @returns {Promise<MemberGroupUi[]>}
 */
export async function fetchAllMemberGroups(accessToken) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    /** @type {unknown[]} */
    const rawRows = [];

    for (let page = 1; page <= MAX_PAGES; page += 1) {
        const params = new URLSearchParams({
            pageNumber: String(page),
            pageSize: String(PAGE_SIZE),
        });
        const url = `${API_BASE_URL}/members/api/Group?${params}`;
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        });

        const { list, metaData } = await parseListResponse(
            res,
            'Gruplar alınamadı',
        );
        rawRows.push(...list);

        const meta = parseNewsListMeta(
            metaData,
            list.length,
            page,
            PAGE_SIZE,
        );
        if (!meta.hasNext || list.length === 0) break;
    }

    /** @type {MemberGroupUi[]} */
    const groups = [];
    for (const row of rawRows) {
        const mapped = mapApiGroupToUi(row);
        if (mapped) groups.push(mapped);
    }

    groups.sort((a, b) =>
        a.name.localeCompare(b.name, 'tr-TR', {
            sensitivity: 'base',
            numeric: true,
        }),
    );

    return groups;
}

const GROUP_SEARCH_PAGE_SIZE = 500;

/**
 * `GET .../members/api/Group/search?keyword=&pageNumber=&pageSize=`
 * @param {string} accessToken
 * @param {string} keyword
 * @param {{ signal?: AbortSignal, pageSize?: number }} [opts]
 * @returns {Promise<MemberGroupUi[]>}
 */
export async function searchMemberGroups(accessToken, keyword, opts = {}) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const k = keyword.trim();
    if (!k) {
        return [];
    }

    const pageSize = opts.pageSize ?? GROUP_SEARCH_PAGE_SIZE;
    /** @type {MemberGroupUi[]} */
    const out = [];

    for (let page = 1; page <= MAX_PAGES; page += 1) {
        const params = new URLSearchParams({
            keyword: k,
            pageNumber: String(page),
            pageSize: String(pageSize),
        });
        const url = `${API_BASE_URL}/members/api/Group/search?${params}`;
        const res = await fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            signal: opts.signal,
        });

        const { list, metaData } = await parseListResponse(
            res,
            'Grup araması yapılamadı',
        );

        for (const row of list) {
            const mapped = mapApiGroupToUi(row);
            if (mapped) out.push(mapped);
        }

        const meta = parseNewsListMeta(
            metaData,
            list.length,
            page,
            pageSize,
        );
        if (!meta.hasNext || list.length === 0) break;
    }

    out.sort((a, b) =>
        a.name.localeCompare(b.name, 'tr-TR', {
            sensitivity: 'base',
            numeric: true,
        }),
    );

    return out;
}
